import React from 'react';

import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {Plugins, Atom, type AtomRenderProps} from '@packages/systems/wfdl';
import {TTypeConstructor, TTypeApplication} from '@packages/systems/wfdl-ast';
import {useWidgetConfig} from '@packages/shared/helpers/WidgetHelper';
import {extraAtomFields} from '@packages/shared/constants/Atoms';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';
import {
  MENU_STATE_HIDE,
  TEMP_DROPDOWN_MENU_PATH,
} from '@packages/utilities/expression-path-constants';
import {
  EvaluatedNodeData,
  getChildren,
  getInPojo,
  getStringOrElse,
} from '@packages/utilities/data-utils';

const dropdownMenuDataPath = TEMP_DROPDOWN_MENU_PATH.map(
  (segment) => segment.at
);

const getMenuState = (data: EvaluatedNodeData) =>
  getStringOrElse(getInPojo(data, dropdownMenuDataPath), MENU_STATE_HIDE);

const className = 'w-dropdown';

const DropdownWrapper = Atom.createRender(
  ({
    attributes,
    classes,
    generateAttributes,
    data,
    renderChild,
    context,
  }: AtomRenderProps) => {
    const runtimeEnvironment = useRuntimeEnvironment();
    const previewMode = usePreviewMode();
    const nativeNodeRef = useWidgetConfig(
      className,
      runtimeEnvironment,
      previewMode
    );
    const menu = getMenuState(data);
    const [menuState, setMenuState] = React.useState(menu);
    React.useEffect(() => {
      if (menu !== menuState) {
        setMenuState(menu);
      }
    }, [menu, menuState]);

    const updatedContext = context.set('menu', menuState);
    const dropdownWrapperClasses = classes.push(className);

    return (
      <div
        ref={nativeNodeRef}
        {...generateAttributes(attributes, dropdownWrapperClasses)}
      >
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, updatedContext)
        )}
      </div>
    );
  }
);

const config = Atom.create({
  displayName: 'Dropdown',
  constraints: {
    wraps: ['DropdownToggle', 'DropdownList'],
  },
  showEmpty: true,
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Dropdown'), iconName('Dropdown')),
  },
  render: DropdownWrapper,
  dataType: TTypeApplication({
    con: TTypeConstructor(
      qualify(Plugins.name('Dropdown'), typeName('DropdownWrapper'))
    ),
    arg: extraAtomFields,
  }),
  showSettingsInDescendants: true,
  hideAncestorSettings: true,
});

export default config;
